/**
 * EC-5 — the interactive acquisition loop: one bounded POST per missing resource, then polling the
 * governed status door until it settles
 * (`docs/specs/strategic-architecture/governed-email-content-retrieval-spec.md` §3.1, §3.2, §9).
 *
 * The shape of this file is dictated by four rules that are easy to state and easy to violate:
 *
 *  1. **ONE attempt per open, per resource.** Not one per render, not one per poll. A POST takes a
 *     claim and spends a Graph call; firing it from an effect that re-runs on every status refresh
 *     is how an open message turns into a retry storm that rate-limits its own reader out of the
 *     answer. The attempted set is a ref, keyed by the resource, and it is reset only when the
 *     email or the principal changes.
 *  2. **Polling is bounded and slow.** No faster than every 2 s, and it stops at the 30 s
 *     interactive waiting budget, on unmount, or as soon as the resource stops being in flight.
 *  3. **`can_fetch` is the SERVER's answer.** This hook never decides eligibility; it reads the
 *     envelope. A `retry_later` is retried only when a human clicks Retry, with the server's own
 *     delay observed — never automatically.
 *  4. **Bytes are never fetched here.** Files load on an explicit View/Download, one file, no
 *     siblings (spec §3.1).
 *
 * ⛔ Nothing in this module runs for a collapsed row: the component that uses it mounts on expand.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { apiFetch, ApiError } from '../api/client';
import type { CrmContentFetchResult, CrmEmailContent } from '../types';

/** Spec §10: the interactive attempt budget. Polling stops here whatever the server is doing. */
export const WAITING_BUDGET_MS = 30_000;
/** Spec §9: "Poll no faster than two seconds." */
export const MIN_POLL_MS = 2_000;

/** The resources a caller can ask to have acquired. `attachment` carries an id. */
export type FetchResource = 'body' | 'manifest' | { attachment: number };

const resourceKey = (r: FetchResource) =>
  (typeof r === 'string' ? r : `attachment:${r.attachment}`);

/** A resource is "in flight" while the server says an attempt owns it or a scan is pending. */
export const IN_FLIGHT_STATES = new Set(['fetching', 'scan_pending']);

/**
 * The POST door, as a plain function rather than a mutation: it is fired from an effect and from a
 * click, and both need the same one-shot semantics without React Query's retry/dedup behaviour
 * anywhere near a call that takes a claim.
 *
 * `base` is the surface's OWN door — the generic email one, or the record-context one. Passing it
 * in is what keeps the record surface from ever falling back to the generic route (spec §9: "The UI
 * never substitutes the generic email route to avoid a denied record route").
 */
export async function postContentFetch(
  base: string, resource: FetchResource,
): Promise<CrmContentFetchResult> {
  const payload = typeof resource === 'string'
    ? { resource }
    : { resource: 'attachment', attachment_id: resource.attachment };
  return apiFetch<CrmContentFetchResult>(`${base}/fetch`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export interface OnDemandState {
  /** Resource keys with an attempt this component started and has not seen settle. */
  pending: Set<string>;
  /** The server's delay for a `retry_later`, per resource key — shown, never auto-consumed. */
  retryAfter: Record<string, number | null>;
  /** True once the 30 s waiting budget lapsed with the resource still unsettled. */
  timedOut: Set<string>;
  /** The budget has been spent for this open: polling stopped and the surface offers a Refresh. */
  budgetSpent: boolean;
  /** Re-read the status door once, by hand, and restart the budget — the manual replacement for
   *  the polling this build refuses to do for ever. */
  refresh: () => void;
  /** Fire ONE acquisition for a resource (the explicit Retry button, and View/Download). */
  fetchNow: (resource: FetchResource) => void;
  /** Whether this component already attempted a resource during this open. */
  attempted: (resource: FetchResource) => boolean;
}

export interface UseContentOnDemandArgs {
  /** The surface's own content door, e.g. `/api/crm/email/9/content`. */
  base: string;
  /** Identity of THIS open: changing it resets every attempt and every timer. */
  openKey: string;
  /** The current envelope, or undefined while it is being authorized. */
  content?: CrmEmailContent;
  /** The query key to invalidate so the status door is re-read. */
  statusKey: readonly unknown[];
  /** False while the surface is collapsed/closed — nothing fires, nothing polls. */
  enabled: boolean;
  /** Other queries this surface renders from the same content, invalidated alongside the status
   *  door — e.g. the record surface's evidence BODY, which serves the CRM body once an acquisition
   *  lands. Without it a recovered body sits unseen until the next open. */
  extraInvalidateKeys?: readonly (readonly unknown[])[];
}

/**
 * Drive the acquisition loop for one open email.
 *
 * On open, for each of body and manifest: if the envelope says `not_captured` **and** `can_fetch`,
 * fire exactly one POST. Then, while anything this component started is in flight, re-read the
 * status door on a ≥2 s cadence until it settles or the budget lapses.
 */
export function useCrmContentOnDemand({
  base, openKey, content, statusKey, enabled, extraInvalidateKeys,
}: UseContentOnDemandArgs): OnDemandState {
  const qc = useQueryClient();
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  // ⛔ ONE STATE OBJECT, STAMPED WITH THE OPEN IT BELONGS TO. Everything per-open lives here — the
  // attempted set (the "one POST per resource per open" rule), what is pending, the server's retry
  // delays and the budget clock — so a new open replaces all of it atomically and none of it can
  // survive into the next message a piece at a time.
  const [session, setSession] = useState<Session>(() => freshSession(openKey));
  const current = session.openKey === openKey;

  // ⛔ THE DEDUP DECISION LIVES IN A REF, NOT IN STATE, AND THAT IS NOT AN OPTIMISATION. "Have I
  // already fired for this resource?" has to be answered SYNCHRONOUSLY, at the moment of the call:
  // React may defer a state updater to the next render, so deciding inside one and reading a flag
  // it set is a race that fires two POSTs — two claims, two Graph calls — for one open. The ref is
  // the source of truth for the decision; `session.attempted` mirrors it for rendering only.
  const attemptedRef = useRef<Set<string>>(new Set());
  const attemptedOpenRef = useRef(openKey);

  useEffect(() => {
    if (session.openKey !== openKey) setSession(freshSession(openKey));
  }, [openKey, session.openKey]);

  const settle = useCallback((key: string, result: CrmContentFetchResult | null) => {
    if (!aliveRef.current) return;
    setSession((prev) => {
      if (prev.openKey !== openKey) return prev;      // a stale attempt never edits a newer open
      const pending = new Set(prev.pending);
      if (!result || !IN_FLIGHT_STATES.has(result.state)) pending.delete(key);
      return {
        ...prev, pending,
        retryAfter: { ...prev.retryAfter, [key]: result?.retry_after_seconds ?? null },
      };
    });
  }, [openKey]);

  const fire = useCallback((resource: FetchResource, force: boolean) => {
    const key = resourceKey(resource);
    if (attemptedOpenRef.current !== openKey) {      // a new open: forget the previous one's attempts
      attemptedOpenRef.current = openKey;
      attemptedRef.current = new Set();
    }
    if (!force && attemptedRef.current.has(key)) return;   // ONE attempt per open, per resource
    attemptedRef.current.add(key);
    setSession((prev) => {
      if (prev.openKey !== openKey) return prev;
      const timedOut = new Set(prev.timedOut);
      timedOut.delete(key);
      return {
        ...prev,
        attempted: new Set(prev.attempted).add(key),
        pending: new Set(prev.pending).add(key),
        startedAt: prev.startedAt || Date.now(),
        timedOut,
      };
    });
    postContentFetch(base, resource)
      .then((result) => {
        settle(key, result);
        void qc.invalidateQueries({ queryKey: statusKey });
        extraInvalidateKeys?.forEach((k) => { void qc.invalidateQueries({ queryKey: k }); });
      })
      .catch((err) => {
        // A 403 is a REFUSAL, not a transient failure, and must never leave a Retry affordance
        // behind (spec §3.2: "never show a Graph retry button after a CRM permission refusal").
        // It settles with no delay, which renders as nothing; the status door's own 403 is what
        // the surface shows instead.
        const status = err instanceof ApiError ? err.status : null;
        settle(key, null);
        if (status != null && status >= 500) {
          // An upstream outage is worth a visible, human-triggered retry — unlike a refusal.
          setSession((prev) => (prev.openKey === openKey
            ? { ...prev, retryAfter: { ...prev.retryAfter, [key]: 30 } } : prev));
        }
        void qc.invalidateQueries({ queryKey: statusKey });
      });
  }, [base, extraInvalidateKeys, openKey, qc, settle, statusKey]);

  // ── the automatic ONE attempt on explicit open (spec §3.1) ──────────────────────────────────
  useEffect(() => {
    if (!enabled || !current) return;
    // ⛔ A RESPONSE THAT IS NOT THE ENVELOPE FIRES NOTHING. An older backend, a stub, a partial
    // shape — none of them is a reason to POST, and reading through a missing `body` would crash
    // the row rather than degrade it. Same guard the shared list applies to the same payload.
    const body = content?.body;
    const att = content?.attachments;
    if (body?.state === 'not_captured' && body.can_fetch) fire('body', false);
    if ((att?.state === 'not_captured' || att?.state === 'partial') && att.can_fetch) {
      fire('manifest', false);
    }
  }, [enabled, content, current, fire]);

  // ── bounded polling while something is still in flight ──────────────────────────────────────
  //
  // ⛔ **IN FLIGHT IS NOT THE SAME AS "WE STARTED IT"** (review MAJOR 2). `fetching` and
  // `scan_pending` come from the SERVER and can be true when this surface has POSTed nothing at
  // all — another reader's acquisition, a worker's, or, most likely of all, EVERY file reading
  // `scan_pending` because the app identity cannot read blob index tags. The first cut started the
  // budget clock only inside `fire()`, so in exactly that case `startedAt` stayed 0, the budget was
  // never reached, and every expanded row polled every two seconds for as long as it stayed open —
  // on the record surface re-running `record_evidence` each time. The clock now starts when the
  // loop first OBSERVES in-flight work, whoever started it, and the budget stops the polling
  // either way.
  const inFlight = current && (
    session.pending.size > 0
    || IN_FLIGHT_STATES.has(content?.body?.state ?? '')
    || (content?.attachments?.items?.some((i) => IN_FLIGHT_STATES.has(i.state)) ?? false));

  useEffect(() => {
    if (!enabled || !inFlight || session.budgetSpent) return undefined;

    // ⛔ **THE TIMER RESCHEDULES ITSELF; IT DOES NOT RELY ON THE EFFECT RE-RUNNING.** The first
    // cut scheduled ONE timeout and depended on `content` changing identity to schedule the next —
    // which React Query's structural sharing makes untrue precisely when it matters: a poll that
    // returns the same envelope hands back the SAME object, the effect does not re-run, and the
    // loop either stalls after one tick or (when something else re-renders it) never reaches the
    // budget check. Driving the cadence from the callback makes both the polling and its stop
    // depend only on the clock.
    let cancelled = false;
    const startedAt = session.startedAt || Date.now();
    if (!session.startedAt) {
      setSession((prev) => (prev.openKey === openKey && !prev.startedAt
        ? { ...prev, startedAt } : prev));
    }

    const tick = () => {
      if (cancelled) return;
      if (Date.now() - startedAt >= WAITING_BUDGET_MS) {
        // The budget is a STOP, not a failure: whatever the server is doing carries on, and the
        // next deliberate open re-reads it. What must not carry on is this tab polling for ever.
        // Whatever is still unsettled is marked timed out, which is what puts a manual Refresh on
        // screen instead of a spinner that resolves for nobody.
        setSession((prev) => (prev.openKey === openKey && !prev.budgetSpent
          ? { ...prev, budgetSpent: true, timedOut: new Set(prev.pending), pending: new Set() }
          : prev));
        return;
      }
      void qc.invalidateQueries({ queryKey: statusKey });
      // ⛔ **AND EVERYTHING THAT DISPLAYS WHAT THE DOOR IS TALKING ABOUT.** This is what repaints
      // a body someone ELSE fetched: `settle` covers our own POST, but an acquisition started by
      // another tab, another reader or a worker reaches this surface only as a state change on a
      // POLL — and the body lives in a different query against a different door, which would
      // otherwise go on serving the 404 it cached before the fetch (Release B canary, msg 494083).
      extraInvalidateKeys?.forEach((k) => { void qc.invalidateQueries({ queryKey: k }); });
      timer = setTimeout(tick, MIN_POLL_MS);
    };
    let timer = setTimeout(tick, MIN_POLL_MS);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [enabled, inFlight, openKey, session.startedAt, session.budgetSpent, qc, statusKey,
    extraInvalidateKeys]);

  const empty = EMPTY_SESSION;
  const view = current ? session : empty;
  return {
    pending: view.pending,
    retryAfter: view.retryAfter,
    timedOut: view.timedOut,
    budgetSpent: view.budgetSpent,
    refresh: () => {
      setSession((prev) => (prev.openKey === openKey
        ? { ...prev, startedAt: 0, budgetSpent: false, timedOut: new Set() } : prev));
      void qc.invalidateQueries({ queryKey: statusKey });
      extraInvalidateKeys?.forEach((k) => { void qc.invalidateQueries({ queryKey: k }); });
    },
    fetchNow: (resource: FetchResource) => fire(resource, true),
    attempted: (resource: FetchResource) => view.attempted.has(resourceKey(resource)),
  };
}

interface Session {
  openKey: string;
  /** When this open first had in-flight work to wait for — from a POST of ours OR from the server
   *  reporting someone else's. 0 = the clock has not started. */
  startedAt: number;
  /** True once the 30 s budget has been spent for this open, so the stop happens exactly once. */
  budgetSpent: boolean;
  attempted: Set<string>;
  pending: Set<string>;
  retryAfter: Record<string, number | null>;
  timedOut: Set<string>;
}

const freshSession = (openKey: string): Session => ({
  openKey, startedAt: 0, budgetSpent: false, attempted: new Set(), pending: new Set(),
  retryAfter: {}, timedOut: new Set(),
});

/** The view a caller gets for an open this hook has not caught up with yet — nothing pending,
 *  nothing attempted. Never the PREVIOUS open's state. */
const EMPTY_SESSION: Session = freshSession('');
